import { useMemo } from 'react';
import Icon from '../Icon';
import DropdownCard from '../dropdownV2/DropdownCard';
import UserMultiSelector from '../UserMultiSelector';
import TextWithIcon from '../typography/TextWithIcon';
import { assetTypeIcons, customColors, vehicleString } from '../../utilities';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import styles from './TaskVehicles.module.scss';

export default function TaskVehicles({
  vehicles = [],
  vehicleAssigneeOverrides = {},
  onUserAdded,
  onUserRemoved,
  subtype = '',
  workspace = null,
}) {
  const { companyPermissions } = useWorkspace();
  const { companyAdmins } = useWorkspacePermissions();

  const vehicleUsersMap = useMemo(() => {
    return vehicles.reduce((acc, v) => {
      if (!v.workspaces?.length) {
        acc[v.id] = null;
      } else {
        const wsIds = new Set(v.workspaces.map((w) => w.id));
        const wsUsers = companyPermissions
          .filter((p) => wsIds.has(p.workspaceId))
          .map((p) => p.user);
        const userMap = new Map();
        [...(companyAdmins ?? []), ...wsUsers].forEach((u) =>
          userMap.set(u.id, u),
        );
        acc[v.id] = Array.from(userMap.values());
      }
      return acc;
    }, {});
  }, [vehicles, companyPermissions, companyAdmins]);
  return (
    <div className={styles.vehicleList}>
      {vehicles.map((v) => {
        const overrideAssignees = vehicleAssigneeOverrides[v.id] ?? [];
        const hasAssignees = overrideAssignees.length > 0;
        const title = subtype
          ? vehicleString(v)
          : `${vehicleString(v)} (${v.subtype ?? ''})`;
        const assigned = v.assignees?.length
          ? v.assignees.map((a) => `${a.firstName} ${a.lastName}`).join(', ')
          : 'No Assignee(s)';

        const workspaces = v.workspaces?.length
          ? v.workspaces?.map((w) => w.title)?.join(', ')
          : 'No Workspace(s)';

        return (
          <div key={v.id} className={styles.vehicleRow}>
            <div className={styles.left}>
              <Icon
                style={{
                  fontSize: '1.5rem',
                  color: hasAssignees ? customColors.GREEN : customColors.RED,
                }}
              >
                {hasAssignees ? 'check_circle' : 'cancel'}
              </Icon>
              <div className={styles.cardWrap}>
                <DropdownCard
                  size="sm"
                  img={v.imageUrl ?? null}
                  title={title}
                  icon={assetTypeIcons[v.type] || 'local_shipping'}
                  iconProps={{ color: customColors.SECONDARY }}
                  weight="semiBold"
                  xSmallText={`${assigned}`}
                  smallText={`${workspaces}`}
                  readOnly
                  smallIcon="business"
                  xSmallIcon="person"
                  shared={false}
                />
              </div>
            </div>
            <div className={styles.right}>
              {!hasAssignees && (
                <TextWithIcon
                  icon="error"
                  size="xs"
                  noMargin
                  color="red"
                  iconProps={{ fontSize: '1rem' }}
                >
                  Unassigned
                </TextWithIcon>
              )}
              <UserMultiSelector
                otherWorkspace={
                  workspace?.id === 'company_shared' ? null : workspace
                }
                iconDisplay
                disableSelectAll
                hideAdd
                allowedUsers={vehicleUsersMap[v.id]}
                selected={overrideAssignees}
                onUserAdded={(user) =>
                  onUserAdded?.(v.id, user, overrideAssignees)
                }
                onUserRemoved={(user) =>
                  onUserRemoved?.(v.id, user, overrideAssignees)
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
